/**
 * CashFlow — Admin dashboard showing revenue metrics + outstanding + recent payments.
 */
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/config";
import { toast } from "sonner";
import { TrendingUp, Clock, CreditCard, Landmark, Wallet, RefreshCw, Loader2, Trash2, AlertTriangle } from "lucide-react";

const METHOD_META = {
  stripe:   { label: "Carta di credito · Stripe", icon: CreditCard, color: "text-[#635bff]" },
  paypal:   { label: "PayPal",                    icon: Wallet,     color: "text-[#0070ba]" },
  bonifico: { label: "Bonifico bancario",         icon: Landmark,   color: "text-[#ff4b00]" },
  unknown:  { label: "Non classificato",          icon: Wallet,     color: "text-white" },
};

const fmtDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const fmtEur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);

const KpiCard = ({ label, value, sub, icon: Icon, accent = false, testId }) => (
  <div className={`p-6 border ${accent ? "border-[#ff4b00] bg-[#ff4b00]/5" : "border-[#1a1a1a] bg-[#0a0a0a]"}`} data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <span className="font-mono-tech text-[10px] text-white uppercase">{label}</span>
      {Icon && <Icon size={14} className={accent ? "text-[#ff4b00]" : "text-white"} />}
    </div>
    <div className={`font-display text-3xl md:text-4xl ${accent ? "text-[#ff4b00]" : "text-white"}`}>{value}</div>
    {sub && <div className="font-mono-tech text-[10px] text-white mt-2">{sub}</div>}
  </div>
);

export default function CashFlow() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/cash-flow");
      setData(r.data);
    } catch (err) {
      toast.error(errMsg(err, "Errore caricamento cash flow"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const confirmBonifico = async (quoteId) => {
    if (!window.confirm("Confermi di aver ricevuto il bonifico? Il cliente riceverà un'email di conferma.")) return;
    setConfirming(quoteId);
    try {
      await api.post(`/admin/quotes/${quoteId}/confirm-bank-transfer`);
      toast.success("Bonifico confermato · email inviata al cliente");
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Errore"));
    } finally {
      setConfirming(null);
    }
  };

  const removePayment = async (payment) => {
    const label = `${payment.customer_name || payment.customer_email || "—"} · ${fmtEur(payment.amount)}`;
    if (!window.confirm(
      `Rimuovere questo pagamento dal cash flow?\n\n${label}\n\nIl preventivo tornerà in stato "approvato" — non verrà eliminato. Puoi rimarcarlo come pagato in futuro.`
    )) return;
    setRemoving(payment.quote_id);
    try {
      await api.delete(`/admin/cash-flow/payments/${payment.quote_id}`);
      toast.success("Pagamento rimosso dal cash flow");
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Errore rimozione pagamento"));
    } finally {
      setRemoving(null);
    }
  };

  const resetAll = async () => {
    const totalPaid = data.revenue.total.count;
    const input = window.prompt(
      `Stai per AZZERARE tutti i pagamenti dal cash flow (${totalPaid} preventivi pagati + tutte le transazioni Stripe/PayPal registrate).\n\n` +
      `I preventivi torneranno in stato "approvato" — non verranno eliminati.\n` +
      `Anche il KPI "Ricavo (€)" in cima alla dashboard tornerà a 0.\n\n` +
      `Per confermare, scrivi esattamente:  AZZERA`
    );
    if (input !== "AZZERA") {
      toast.info("Operazione annullata");
      return;
    }
    setResetting(true);
    try {
      const r = await api.post("/admin/cash-flow/reset?confirm=RESET-CASH-FLOW");
      const total = (r.data.cleared || 0) + (r.data.transactions_removed || 0);
      if (total === 0) toast.info("Nessun pagamento da azzerare — cash flow già pulito");
      else toast.success(`Cash flow azzerato · ${r.data.cleared} preventivi e ${r.data.transactions_removed} transazioni rimosse`);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Errore azzeramento"));
    } finally {
      setResetting(false);
    }
  };

  if (loading || !data) return <div className="text-white font-mono-tech text-xs">Caricamento cash flow…</div>;

  const r = data.revenue;
  const out = data.outstanding;

  return (
    <div data-testid="cash-flow-dashboard" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono-tech text-[10px] text-white uppercase">◈ Cash Flow</div>
          <div className="font-display text-3xl md:text-4xl mt-1">Movimenti finanziari</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={load}
            data-testid="cash-flow-refresh"
            className="btn-ghost text-xs flex items-center gap-2"
          >
            <RefreshCw size={12} /> Aggiorna
          </button>
          <button
            onClick={resetAll}
            disabled={resetting}
            data-testid="cash-flow-reset-all"
            className="text-xs flex items-center gap-2 border border-[#ef4444]/40 bg-[#ef4444]/5 text-[#ef4444] hover:bg-[#ef4444] hover:text-white px-3 py-2 transition-colors font-mono-tech uppercase tracking-wider"
            title="Rimuove tutti i pagamenti dal cash flow e tutte le transazioni Stripe/PayPal"
          >
            {resetting ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
            Azzera tutto
          </button>
        </div>
      </div>

      {/* Revenue KPIs */}
      <div>
        <div className="font-mono-tech text-[10px] text-white uppercase mb-3">Ricavi (incassati)</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard label="Oggi"       value={fmtEur(r.today.total)}   sub={`${r.today.count} pagamenti`}     icon={TrendingUp} testId="kpi-today" />
          <KpiCard label="Questo mese"    value={fmtEur(r.month.total)}   sub={`${r.month.count} pagamenti`}     icon={TrendingUp} testId="kpi-month" />
          <KpiCard label="Trimestre"      value={fmtEur(r.quarter.total)} sub={`${r.quarter.count} pagamenti`}   icon={TrendingUp} testId="kpi-quarter" />
          <KpiCard label="Anno"           value={fmtEur(r.year.total)}    sub={`${r.year.count} pagamenti`}      icon={TrendingUp} testId="kpi-year" />
          <KpiCard label="Totale storico" value={fmtEur(r.total.total)}   sub={`${r.total.count} pagamenti`}     icon={TrendingUp} testId="kpi-total" />
        </div>
      </div>

      {/* Outstanding */}
      <div>
        <div className="font-mono-tech text-[10px] text-white uppercase mb-3">In sospeso (da incassare)</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KpiCard
            label="Totale in sospeso"
            value={fmtEur(out.total)}
            sub={`${out.count} preventivi in attesa`}
            icon={Clock}
            accent
            testId="kpi-outstanding-total"
          />
          <KpiCard
            label="Approvati · pagamento pendente"
            value={fmtEur(out.approved.total)}
            sub={`${out.approved.count} preventivi`}
            icon={Clock}
            testId="kpi-outstanding-approved"
          />
          <KpiCard
            label="In attesa di bonifico"
            value={fmtEur(out.awaiting_bank_transfer.total)}
            sub={`${out.awaiting_bank_transfer.count} preventivi`}
            icon={Landmark}
            testId="kpi-outstanding-bonifico"
          />
        </div>
      </div>

      {/* By method */}
      <div>
        <div className="font-mono-tech text-[10px] text-white uppercase mb-3">Ripartizione per metodo di pagamento</div>
        {data.by_method.length === 0 ? (
          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-8 text-white font-mono-tech text-xs">Ancora nessun pagamento registrato.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.by_method.map((m) => {
              const meta = METHOD_META[m.method] || METHOD_META.unknown;
              const Ic = meta.icon;
              return (
                <div key={m.method} className="border border-[#1a1a1a] bg-[#0a0a0a] p-5" data-testid={`method-${m.method}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Ic size={14} className={meta.color} />
                    <span className="font-mono-tech text-[10px] text-white uppercase">{meta.label}</span>
                  </div>
                  <div className="font-display text-2xl text-white">{fmtEur(m.total)}</div>
                  <div className="font-mono-tech text-[10px] text-white mt-1">{m.count} pagamenti</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Awaiting bonifico list */}
      {data.awaiting_bank_transfer.length > 0 && (
        <div>
          <div className="font-mono-tech text-[10px] text-white uppercase mb-3">Attesa bonifici · da riconciliare manualmente</div>
          <div className="border border-[#ff4b00]/40 bg-[#ff4b00]/5">
            <table className="w-full text-sm" data-testid="awaiting-bonifico-table">
              <thead>
                <tr className="border-b border-[#ff4b00]/30 text-white font-mono-tech text-[10px] uppercase">
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">Causale</th>
                  <th className="text-right px-4 py-3">Importo</th>
                  <th className="text-left px-4 py-3">Richiesto il</th>
                  <th className="text-right px-4 py-3">Azione</th>
                </tr>
              </thead>
              <tbody>
                {data.awaiting_bank_transfer.map((a) => (
                  <tr key={a.quote_id} className="border-b border-[#ff4b00]/10 hover:bg-white/5" data-testid={`awaiting-row-${a.quote_id}`}>
                    <td className="px-4 py-3">
                      <div className="text-white">{a.customer_name || "-"}</div>
                      <div className="font-mono-tech text-[10px] text-white">{a.customer_email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono-tech text-[11px] text-white">{a.causale}</td>
                    <td className="px-4 py-3 text-right font-display text-lg text-[#ff4b00]">{fmtEur(a.amount)}</td>
                    <td className="px-4 py-3 font-mono-tech text-[10px] text-white">{fmtDate(a.requested_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => confirmBonifico(a.quote_id)}
                        disabled={confirming === a.quote_id}
                        data-testid={`awaiting-confirm-${a.quote_id}`}
                        className="btn-primary py-1.5 px-3 text-[10px]"
                      >
                        {confirming === a.quote_id ? <Loader2 size={10} className="animate-spin" /> : "Conferma ricevuto"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent payments */}
      <div>
        <div className="font-mono-tech text-[10px] text-white uppercase mb-3">Ultimi pagamenti ricevuti</div>
        {data.recent_payments.length === 0 ? (
          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-8 text-white font-mono-tech text-xs">Nessun pagamento ancora incassato.</div>
        ) : (
          <div className="border border-[#1a1a1a] bg-[#0a0a0a]">
            <table className="w-full text-sm" data-testid="recent-payments-table">
              <thead>
                <tr className="border-b border-[#1a1a1a] text-white font-mono-tech text-[10px] uppercase">
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">Metodo</th>
                  <th className="text-right px-4 py-3">Importo</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="text-right px-4 py-3 w-16">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_payments.map((p) => {
                  const meta = METHOD_META[p.method] || METHOD_META.unknown;
                  return (
                    <tr key={p.quote_id} className="border-b border-[#141414] hover:bg-white/5" data-testid={`recent-payment-row-${p.quote_id}`}>
                      <td className="px-4 py-3 font-mono-tech text-[11px] text-white">{fmtDate(p.paid_at)}</td>
                      <td className="px-4 py-3">
                        <div className="text-white">{p.customer_name || "-"}</div>
                        <div className="font-mono-tech text-[10px] text-white">{p.customer_email}</div>
                      </td>
                      <td className="px-4 py-3 font-mono-tech text-xs text-white">
                        <span className={meta.color}>◐</span> {meta.label}
                      </td>
                      <td className="px-4 py-3 text-right font-display text-lg text-white">{fmtEur(p.amount)}</td>
                      <td className="px-4 py-3">
                        <span className="font-mono-tech text-[10px] uppercase text-green-400 border border-green-400/40 px-2 py-0.5">{p.status}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => removePayment(p)}
                          disabled={removing === p.quote_id}
                          data-testid={`remove-payment-${p.quote_id}`}
                          className="p-2 text-white hover:text-[#ef4444] hover:bg-[#ef4444]/10 transition-colors"
                          title="Rimuovi dal cash flow"
                        >
                          {removing === p.quote_id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
